'use client';

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Check } from '../../../icons';
import { cn } from '../../../lib/utils';
import { Icon } from '../../icon/icon';
import { checkboxIconSize, checkboxVariants, type CheckboxSize } from './checkbox-variants';

export type CheckboxIndicatorProps = Omit<ComponentPropsWithoutRef<'span'>, 'children'> & {
  checked?: boolean;
  disabled?: boolean;
  size?: CheckboxSize;
};

/**
 * Visual-only checkbox box: no role, no focus handling, no click behaviour.
 * Use it inside another interactive control, such as a menu row, where a real
 * Checkbox would nest a control inside a control.
 */
export const CheckboxIndicator = forwardRef<HTMLSpanElement, CheckboxIndicatorProps>(
  ({ checked = false, disabled = false, size = 'standard', className, ...rest }, ref) => (
    <span
      ref={ref}
      aria-hidden
      data-slot="checkbox-indicator"
      data-state={checked ? 'checked' : 'unchecked'}
      data-disabled={disabled ? '' : undefined}
      className={cn(
        checkboxVariants({ size }),
        'inline-flex items-center justify-center',
        className,
      )}
      {...rest}>
      {checked && (
        <Icon size={checkboxIconSize[size]} icon={Check} className="stroke-2 text-text-inverse" />
      )}
    </span>
  ),
);
CheckboxIndicator.displayName = 'CheckboxIndicator';
